import { defineStore } from 'pinia'
import type { components } from 'schema.ts'

import type {
	IFetchApiAllChats,
	IFetchApiChatAllMessageCount,
	IFetchApiChatMessageAdd,
	IFetchApiChatMessageDelete,
	IFetchApiChatMessageEdit,
	IFetchApiChatMessageEmailNotify,
	IFetchApiChatMessageMarkAsRead,
	IFetchApiChatNoteAdd,
	IFetchApiChatNoteDelete,
	IFetchApiChatNoteUpdate,
	IFetchApiChatPayload
} from '~/portal/api'
import {
	fetchApiAllChats,
	fetchApiChatAllMessageCount,
	fetchApiChatMessageAdd,
	fetchApiChatMessageCount,
	fetchApiChatMessageDelete,
	fetchApiChatMessageEdit,
	fetchApiChatMessageEmailNotify,
	fetchApiChatMessageMarkAsRead,
	fetchApiChatMessages,
	fetchApiChatNoteAdd,
	fetchApiChatNoteDelete,
	fetchApiChatNoteList,
	fetchApiChatNoteSend,
	fetchApiChatNoteUpdate,
	fetchApiChatReadAll
} from '~/portal/api'
import type { IChatStore } from '~/portal/stores'
import type { IChatMessage } from '~/portal/types'

export const useChatStore = defineStore('chatStore', {
	state: (): IChatStore => ({
		countAll: {
			notifications: 0,
			messages: 0,
			projects: []
		},
		count: {},
		noteList: {},
		chatList: [],
		loading: {},
		chatCount: 0,
		_lastFetchAt: {} as Record<number, number>,
		messagesByProject: {} as Record<number, IChatMessage[]>
	}),

	getters: {
		messageCount(state: IChatStore) {
			return (projectId: number) => state.count[projectId]?.messages || 0
		},

		noteListSorted(state: IChatStore) {
			return (projectId: number): components['schemas']['ListNote'][] => {
				return state.noteList[projectId]?.sort((a, b) => {
					const dateA = a.created_at ? new Date(a.created_at) : new Date()
					const dateB = b.created_at ? new Date(b.created_at) : new Date()
					return dateA.getTime() - dateB.getTime()
				})
			}
		},

		chatListSorted(state) {
			return (projectId: number): IChatMessage[] => {
				const fromNav = state.chatList.find(c => c.id === projectId)?.messages
				const fallback = (state as any).messagesByProject?.[projectId]
				const arr = fromNav || fallback || []

				return [...arr].sort((a, b) => {
					const dateA = a.created_at ? new Date(a.created_at) : new Date()
					const dateB = b.created_at ? new Date(b.created_at) : new Date()
					return dateA.getTime() - dateB.getTime()
				})
			}
		},

		notificationAllAmount(state: IChatStore) {
			return state.countAll.notifications
		},
		chatAllMessageAmount(state: IChatStore) {
			return state.countAll.messages
		}
	},

	actions: {
		async fetchChatList(payload: IFetchApiAllChats) {
			const response = await fetchApiAllChats(payload)
			if (!response || !response.results) return

			const prevById = new Map(this.chatList.map((c: any) => [c.id, c]))

			this.chatList = response.results.map((r: any) => ({
				...r,
				messages:
					prevById.get(r.id)?.messages ?? this.messagesByProject[r.id] ?? []
			}))

			this.count = Object.fromEntries(
				this.chatList.map((chat: any) => [
					chat.id,
					{ messages: chat.unseen_messages }
				])
			)
			this.chatCount = response.count || 0
		},

		async fetchChatMessages(payload: IFetchApiChatPayload) {
			const pidRaw = (payload as any).projectId
			const pid = Number(pidRaw)
			if (!Number.isFinite(pid) || pid <= 0) return

			const now = Date.now()
			const lastFetchMap: Record<number, number> =
				this._lastFetchAt || (this._lastFetchAt = {})
			const last = lastFetchMap[pid] ?? 0
			if (now - last < 250) return
			lastFetchMap[pid] = now

			if ((this.loading as any)[pid]) return

			let response, raw
			try {
				;(this.loading as any)[pid] = true
				response = await fetchApiChatMessages({ ...payload, projectId: pid })
				raw = response?.data || []
			} finally {
				;(this.loading as any)[pid] = false
			}
			if (!response || !raw) return

			const normalized = (raw as any[]).map((m: any) => {
				const p = m?.project
				const projId = p && typeof p === 'object' ? Number(p.id) : pid
				const projName = p && typeof p === 'object' ? (p.name ?? '') : ''
				return { ...m, project: projId, projectName: projName }
			})

			const unseenMessages = normalized.filter((m: any) => !m.is_seen).length
			const projNameFromData =
				normalized.find((m: any) => m.project_name)?.project_name || ''

			const cacheExisting = this.messagesByProject[pid] || []
			if (!this.messagesByProject[pid]) {
				this.messagesByProject[pid] = cacheExisting
			}

			const existingById = new Map(
				cacheExisting.map((m: any) => [String(m.id), m])
			)

			let appended = 0
			for (const nm of normalized) {
				const key = String(nm.id)
				const prev = existingById.get(key)
				if (prev) {
					Object.assign(prev, nm)
				} else {
					cacheExisting.push(nm)
					existingById.set(key, nm)
					appended++
				}
			}

			const serverIds = new Set(normalized.map((m: any) => String(m.id)))
			for (let i = cacheExisting.length - 1; i >= 0; i--) {
				const id = String((cacheExisting[i] as any).id)
				if (!serverIds.has(id)) cacheExisting.splice(i, 1)
			}

			if (appended > 0 || cacheExisting.length !== existingById.size) {
				cacheExisting.sort((a: any, b: any) => {
					const da = a.created_at ? new Date(a.created_at).getTime() : 0
					const db = b.created_at ? new Date(b.created_at).getTime() : 0
					return da - db
				})
			}

			const idx = this.chatList.findIndex(c => c.id === pid)
			if (idx !== -1) {
				const chat = this.chatList[idx]
				chat.messages = cacheExisting
				chat.unseen_messages = unseenMessages
				if (projNameFromData && !chat.name) {
					;(chat as any).name = projNameFromData
				}
			}

			;(this.count as any)[pid] = { messages: unseenMessages }
		},

		async markAllAsRead(payload: {
			token: string
			projectId?: number | string
		}) {
			await fetchApiChatReadAll({
				token: payload.token,
				projectId: payload.projectId
			})
		},

		async fetchChatMessageDelete(payload: IFetchApiChatMessageDelete) {
			const pid = Number((payload as any).projectId)
			if (!Number.isFinite(pid) || pid <= 0) return

			const cache = this.messagesByProject[pid] || []
			const idx = cache.findIndex((m: any) => m.id === payload.messageId)
			const toDelete = idx !== -1 ? cache[idx] : undefined

			const res = await fetchApiChatMessageDelete(payload)
			if (!res) return

			if (idx !== -1) cache.splice(idx, 1)

			const chatIdx = this.chatList.findIndex(c => c.id === pid)
			if (chatIdx !== -1) {
				const arr = this.chatList[chatIdx].messages || []
				const i2 = arr.findIndex((m: any) => m.id === payload.messageId)
				if (i2 !== -1) arr.splice(i2, 1)
			}

			if (toDelete && toDelete.is_seen === false) {
				const perProj = (this.count as any)[pid]
				if (perProj) perProj.messages = Math.max(0, (perProj.messages || 0) - 1)
				this.countAll.messages = Math.max(0, (this.countAll.messages || 0) - 1)
			}

			const limit = this.chatList.length || 10
			await Promise.allSettled([
				this.fetchChatMessages({ token: payload.token, projectId: pid }),
				this.fetchChatList({ token: payload.token, params: { limit } }),
				this.fetchApiChatAllMessageCount({ token: payload.token })
			])
		},

		async fetchChatMessageAdd(payload: IFetchApiChatMessageAdd) {
			await fetchApiChatMessageAdd(payload)
			await this.fetchChatMessages({
				token: payload.token,
				projectId: Number((payload as any).projectId)
			})

			const limit = this.chatList.length || 10
			await this.fetchChatList({
				token: payload.token,
				params: { limit }
			})
		},

		async fetchChatMessageEdit (payload: IFetchApiChatMessageEdit) {
			const pid = Number(payload.projectId)
			const response = await fetchApiChatMessageEdit({
				...payload,
				projectId: pid
			})
			if (!response?.data) return

			const updated = response.data as IChatMessage
			const applyPatch = (target: { is_seen?: boolean }): void => {
				Object.assign(target, {
					...updated,
					project: pid,
					is_seen: target.is_seen
				})
			}

			const cached = this.messagesByProject[pid]?.find(
				(m: IChatMessage) => m.id === updated.id
			)
			if (cached) {
				applyPatch(cached)
			}

			const chat = this.chatList.find(c => c.id === pid)
			const listed = chat?.messages?.find((m: any) => m.id === updated.id)
			if (listed && listed !== cached) {
				applyPatch(listed)
			}

			if (!cached && !listed) {
				await this.fetchChatMessages({ token: payload.token, projectId: pid })
			}
		},

		async fetchChatMessageMarkAsRead(payload: IFetchApiChatMessageMarkAsRead) {
			const pid = Number((payload as any).projectId)
			const response = await fetchApiChatMessageMarkAsRead({
				...payload,
				projectId: pid
			})
			if (response) {
				const index = this.chatList.findIndex(chat => chat.id === pid)
				if (index !== -1) {
					const message = this.chatList[index]?.messages?.find(
						({ id }) => id === payload.messageId
					)
					if (message) {
						;(message as any).is_seen = true
						const cnt = (this.count as any)[pid]
						if (cnt?.messages) {
							cnt.messages--
						}
						this.countAll.messages--
					}
				}
				const cache = this.messagesByProject[pid]
				if (cache) {
					const m = cache.find(({ id }) => id === payload.messageId)
					if (m) (m as any).is_seen = true
				}
			}
		},

		async fetchChatMessageCount(payload: IFetchApiChatPayload) {
			const pid = Number((payload as any).projectId)
			const response = await fetchApiChatMessageCount({
				...payload,
				projectId: pid
			})
			if (response && response.data) {
				;(this.count as any)[pid] = response.data
			}
			return response?.data
		},

		async fetchApiChatAllMessageCount(payload: IFetchApiChatAllMessageCount) {
			const response = await fetchApiChatAllMessageCount(payload)
			if (response) {
				this.countAll = response.data
			}
		},

		async fetchChatNoteList(payload: IFetchApiChatPayload) {
			const pid = Number((payload as any).projectId)
			const response = await fetchApiChatNoteList({
				...payload,
				projectId: pid
			})
			if (response && response.data) {
				;(this.noteList as any)[pid] = response.data
			}
		},

		async fetchApiChatNoteAdd(payload: IFetchApiChatNoteAdd) {
			await fetchApiChatNoteAdd(payload)
			await this.fetchChatNoteList({
				token: payload.token,
				projectId: Number((payload as any).projectId)
			})
		},

		async fetchApiChatNoteDelete(payload: IFetchApiChatNoteDelete) {
			await fetchApiChatNoteDelete(payload)
			await this.fetchChatNoteList({
				token: payload.token,
				projectId: Number((payload as any).projectId)
			})
		},

		async fetchApiChatNoteSend(payload: IFetchApiChatNoteDelete) {
			await fetchApiChatNoteSend(payload)
			await this.fetchChatNoteList({
				token: payload.token,
				projectId: Number((payload as any).projectId)
			})
			await this.fetchChatMessages({
				token: payload.token,
				projectId: Number((payload as any).projectId)
			})
		},

		async fetchChatNoteUpdate(payload: IFetchApiChatNoteUpdate) {
			await fetchApiChatNoteUpdate(payload)
			await this.fetchChatNoteList({
				token: payload.token,
				projectId: Number((payload as any).projectId)
			})
		},

		async fetchApiChatMessageEmailNotify(
			payload: IFetchApiChatMessageEmailNotify
		) {
			await fetchApiChatMessageEmailNotify(payload)
			await this.fetchChatMessages({
				token: payload.token,
				projectId: Number((payload as any).projectId)
			})
		},

		decreaseNotificationCount() {
			if (this.countAll.notifications > 0) {
				this.countAll.notifications--
			}
		}
	}
})
